import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import api from "services/api";
import { filterByCountry } from "utils/registrationStates";
import {
  getRequestCountry,
  resolveGeoCountry,
  COUNTRY_CHANGED_EVENT,
} from "services/requestCountry";
import Faqs from "components/Faqs";
import Translator, { __ } from "services/Translator";
import BonusTerms from "./BonusTerms";
import ModalCloseButton from "components/ModalCloseButton";
import { useIsAuthenticated } from "components/auth/AuthGate";
import {
  TeaserHero,
  TeaserSection,
  TeaserGrid,
  FeatureCard,
} from "components/auth/teaser";

// Generic, non-personal value-prop icons (inline SVG — no missing-asset imports).
const IconGift = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="8" width="18" height="4" rx="1" />
    <path d="M12 8v13M5 12v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8" />
    <path d="M12 8S10.5 4 8 4a2 2 0 0 0 0 4h4zm0 0s1.5-4 4-4a2 2 0 0 1 0 4h-4z" />
  </svg>
);
const IconSpins = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12a9 9 0 1 1-3.5-7.1" />
    <path d="M21 4v5h-5" />
  </svg>
);
const IconReload = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="8" />
    <path d="M12 7v5l3 2" />
  </svg>
);
const IconCrown = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 19h16M4 19l-1.5-9L8 13l4-8 4 8 5.5-3L20 19" />
  </svg>
);

const PROMO_API = "/cms/promotions-page-cards/true";
const PROMO_PARAMS = { sort: "sortOrder,ASC", allFields: true };

/**
 * Marketing fallback shown when there are no live promo cards. A prospect must
 * never hit a "No promotions available" dead-end: for a logged-out visitor this
 * is a real acquisition surface (welcome-offer hero + working Sign-Up CTA +
 * generic, non-personal value props). For a signed-in member with no live
 * offers it degrades to a calm "on the way" message (no signup CTA).
 */
function PromotionsFallback({ isAuthenticated, onBonusTerms, cta }) {
  if (isAuthenticated) {
    return (
      <div className="promotions_fallback promotions_fallback--member">
        <div className="promotions_fallback__panel">
          <span className="teaser-eyebrow">{__("Promotions")}</span>
          <h2 className="promotions_fallback__title">
            {__("Fresh offers are on the way")}
          </h2>
          <p className="promotions_fallback__sub">
            {__(
              "Your next bonus is being prepared. Check back soon for new promotions, free spins and reload rewards."
            )}
          </p>
          <button
            type="button"
            className="teaser-btn teaser-btn--ghost"
            onClick={onBonusTerms}
          >
            {__("View Bonus Terms")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="promotions_fallback">
      <TeaserHero
        align="center"
        eyebrow={__("Welcome Offer")}
        title={__("Bonuses built for real play")}
        subtitle={__(
          "Join today to unlock your Welcome Bonus — then keep the momentum going with free spins, reload offers and VIP rewards."
        )}
        signUpLabel={__("Claim Your Bonus")}
        {...cta}
      />

      <TeaserSection
        align="center"
        title={__("What you can look forward to")}
      >
        <TeaserGrid minColWidth={220} gap={18}>
          <FeatureCard
            icon={IconGift}
            title={__("Welcome Bonus")}
            description={__(
              "Start strong with a boosted first deposit the moment you join."
            )}
          />
          <FeatureCard
            icon={IconSpins}
            title={__("Free Spins")}
            description={__(
              "Spin the reels on hand-picked slots with regular free-spin drops."
            )}
          />
          <FeatureCard
            icon={IconReload}
            title={__("Reload Offers")}
            description={__(
              "Top-up rewards that keep every deposit working harder for you."
            )}
          />
          <FeatureCard
            icon={IconCrown}
            title={__("VIP Rewards")}
            description={__(
              "Climb the tiers for cashback, faster withdrawals and exclusive perks."
            )}
          />
        </TeaserGrid>
      </TeaserSection>

      <p className="promotions_fallback__legal">
        {__("18+. Play responsibly. Terms & Conditions apply.")}{" "}
        <button
          type="button"
          className="promotions_fallback__terms-link"
          onClick={onBonusTerms}
        >
          {__("View Bonus Terms")}
        </button>
      </p>
    </div>
  );
}

export default function Promotions(props) {
  const isAuthenticated = useIsAuthenticated();
  const langCode = Translator.getSelectedLanguage().code;

  // Responsive hook (play365 design)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [promotionsBanner, setPromotionsBanner] = useState(null);
  const [promotionCards, setPromotionCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [showBonusTermsModal, setShowBonusTermsModal] = useState(false);
  // Ignore out-of-order fetches when the country changes mid-flight (see banner).
  const promoSeqRef = useRef(0);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchBanner = async (visitorCountry, seq) => {
      try {
        const res = await api.get("/cms/promotions-page-banner/true", {
          sort: "sortOrder,ASC",
        });
        if (seq !== promoSeqRef.current) return;
        if (res.status === 200) {
          const banners = filterByCountry(res.data.data || [], visitorCountry);
          setPromotionsBanner(banners[0] || null);
        }
      } catch (_) {}
    };
    const fetchCards = async (visitorCountry, seq) => {
      try {
        setLoading(true);
        const res = await api.get(PROMO_API, PROMO_PARAMS);
        if (seq !== promoSeqRef.current) return;
        if (res.status === 200) {
          const data = res.data?.data ?? res.data ?? [];
          setPromotionCards(filterByCountry(Array.isArray(data) ? data : [], visitorCountry));
        } else {
          setPromotionCards([]);
        }
      } catch (error) {
        console.error("Error fetching promotions:", error);
        if (seq === promoSeqRef.current) setPromotionCards([]);
      } finally {
        if (seq === promoSeqRef.current) setLoading(false);
      }
    };
    const load = async () => {
      const seq = ++promoSeqRef.current;
      // Resolve the effective country (manual selector -> account -> geo) so the
      // manual override is honoured, consistent with the X-Request-Country header.
      await resolveGeoCountry(api);
      const visitorCountry = getRequestCountry();
      fetchBanner(visitorCountry, seq);
      fetchCards(visitorCountry, seq);
    };
    load();
    // Refetch in place when the country changes — no page reload.
    const onChange = () => load();
    window.addEventListener(COUNTRY_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(COUNTRY_CHANGED_EVENT, onChange);
  }, []);

  const renderHTML = (rawHTML) =>
    rawHTML
      ? React.createElement("div", {
          dangerouslySetInnerHTML: { __html: rawHTML },
          className: "promotion_details_content_inner",
        })
      : null;

  const resolvePromoImage = (image) => {
    if (!image) return "";
    if (typeof image === "string") {
      if (image.startsWith("http") || image.startsWith("/")) return image;
      return image;
    }
    return "";
  };

  const getCardImage = (card) =>
    resolvePromoImage(card.backgroundImage || card.image);
  const getCardHeadline = (card) => card.headline || "";
  const getCardDescription = (card) => card.description || "";

  const stripHtmlTags = (html) => {
    if (!html) return "";
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || "";
  };

  const renderMultiColorTitle = (title) => {
    if (!title) return null;
    const words = title.split(/\s+/).filter((word) => word.trim());
    const MAX_FIRST_LINE_LENGTH = 12;
    if (words.length === 1) {
      return (
        <h3 className="promotion_title promotion_title_multicolor">
          <span className="title_line title_number">{words[0]}</span>
        </h3>
      );
    }
    let firstLine = words[0];
    let remainingIndex = 1;
    for (let i = 1; i < words.length; i++) {
      const testLine = firstLine + " " + words[i];
      if (testLine.length <= MAX_FIRST_LINE_LENGTH) {
        firstLine = testLine;
        remainingIndex = i + 1;
      } else break;
    }
    const secondLine = words.slice(remainingIndex).join(" ");
    return (
      <h3 className="promotion_title promotion_title_multicolor">
        <span className="title_line title_number">{firstLine}</span>
        {secondLine && (
          <span className="title_line title_text">{secondLine}</span>
        )}
      </h3>
    );
  };

  const displayBanner = promotionsBanner;
  const isPromoEnabled = (p) => {
    const v = p && p.isEnabled;
    return v && v !== "0" && v !== "false" && v !== "[]";
  };
  const visibleCards = (promotionCards || []).filter(isPromoEnabled);
  const hasCards = visibleCards.length > 0;

  const teaserCta = {
    setModalSignUpPropsData: props.setModalSignUpPropsData,
    setModalLoginPropsData: props.setModalLoginPropsData,
  };

  const openPromoModal = (promo) => {
    setSelectedPromotion(promo);
    setShowPromotionModal(true);
  };

  const handleClaimBonusClick = () => {
    // Close the promotion details modal first
    setShowPromotionModal(false);

    if (isAuthenticated) {
      // Open cashier (deposit) modal if handler is available
      if (typeof props.setOpenWalletModalTab === "function") {
        props.setOpenWalletModalTab("deposit");
      }
      return;
    }

    // Not authenticated: open signup modal if handler is available
    if (typeof props.setModalSignUpPropsData === "function") {
      props.setModalSignUpPropsData(true);
      setTimeout(() => {
        // Reset flag after a short delay, following existing pattern
        props.setModalSignUpPropsData(false);
      }, 500);
    }
  };

  return (
    <>
      <div className="main_content_area">
        <div className="container-xxl">
          <div
            className="promotions_area"
            style={{
              paddingLeft: isMobile ? "10px" : "0px",
              paddingRight: isMobile ? "10px" : "0px",
            }}
          >
            {displayBanner && displayBanner.imageLargeDesktop && (
              <div className="promotions_banner">
                <picture>
                  <source
                    srcSet={displayBanner.imageMobile}
                    media="(max-width: 600px)"
                  />
                  <source
                    srcSet={displayBanner.imageTablet}
                    media="(max-width: 900px)"
                  />
                  <source
                    srcSet={displayBanner.imageMediumDesktop}
                    media="(max-width: 1200px)"
                  />
                  <source
                    srcSet={displayBanner.imageLargeDesktop}
                    media="(min-width: 1201px)"
                  />
                  <img
                    src={
                      displayBanner.imageMediumDesktop ||
                      displayBanner.imageLargeDesktop
                    }
                    alt={__("Promotions Banner")}
                    className="promotions_banner_image"
                  />
                </picture>
              </div>
            )}
            <div className="promotions_heading">
              <h1 className="promotions_heading_text">
                {displayBanner && displayBanner.heading
                  ? displayBanner.heading
                  : __("Promotions")}{" "}
                {displayBanner && displayBanner.subHeading && (
                  <span>{displayBanner.subHeading}</span>
                )}
              </h1>
              <h2 className="promotions_sub_heading_text">
                {displayBanner && displayBanner.description
                  ? displayBanner.description
                  : __(
                      "Discover the latest bonuses, free spins and rewards — new offers added regularly."
                    )}
              </h2>
            </div>

            <div className="promotions_item_area">
              {loading ? (
                <div className="promotions_loading">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{__("Loading...")}</span>
                  </div>
                  <p className="loading_text">
                    {__("Loading promotions...")}
                  </p>
                </div>
              ) : hasCards ? (
                <div className="promotions_grid">
                  {visibleCards
                      .map((promotion, index) => {
                        const imgSrc = getCardImage(promotion);
                        const headline = getCardHeadline(promotion);
                        const desc = getCardDescription(promotion);
                        const descText =
                          typeof desc === "string"
                            ? /<[a-z][\s\S]*>/i.test(desc)
                              ? stripHtmlTags(desc)
                              : desc
                            : "";
                        return (
                          <div
                            key={promotion.id || `promo-${index}`}
                            className="promotion_card"
                            role="button"
                            tabIndex={0}
                            onClick={() => openPromoModal(promotion)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                openPromoModal(promotion);
                              }
                            }}
                            onTouchStart={(e) => {
                              e.currentTarget.style.transform = "scale(0.98)";
                            }}
                            onTouchEnd={(e) => {
                              e.currentTarget.style.transform = "";
                            }}
                            onTouchCancel={(e) => {
                              e.currentTarget.style.transform = "";
                            }}
                            aria-label={`${__("View promotion")}: ${headline}`}
                          >
                            <div className="promotion_image_container">
                              {imgSrc && (
                                <img
                                  src={imgSrc}
                                  alt={headline}
                                  className={`promotion_image ${(headline || "")
                                    .replace(/\s+/g, "-")}`}
                                  loading="lazy"
                                />
                              )}
                              <div className="promotion_image_overlay">
                                {renderMultiColorTitle(
                                  promotion.bannerHeading
                                )}
                              </div>
                            </div>
                            <div className="promotion_content">
                              <h3 className="promotion_title">{headline}</h3>
                              <p className="promotion_description">
                                {descText}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                </div>
              ) : (
                <PromotionsFallback
                  isAuthenticated={isAuthenticated}
                  onBonusTerms={() => setShowBonusTermsModal(true)}
                  cta={teaserCta}
                />
              )}
            </div>

            {hasCards && (
              <center>
                <button
                  type="button"
                  onClick={() => setShowBonusTermsModal(true)}
                  className="commonBtn pOneBtn"
                  style={{ marginTop: "20px" }}
                >
                  {__("View Bonus Terms")}
                </button>
              </center>
            )}
          </div>
          <Faqs heading={true} {...props} />
        </div>
      </div>

      {/* Bonus Terms modal – reuse BonusTerms page content, not fullscreen */}
      <Modal
        show={showBonusTermsModal}
        centered
        size="lg"
        onHide={() => setShowBonusTermsModal(false)}
      >
        <Modal.Body>
          <ModalCloseButton onClick={() => setShowBonusTermsModal(false)} />
          <div className="bonusTerms container-xl">
            <BonusTerms {...props} />
          </div>
        </Modal.Body>
      </Modal>

      {/* Promotion details modal – same look as zino */}
      <Modal
        show={showPromotionModal}
        onHide={() => setShowPromotionModal(false)}
        centered
        dialogClassName="promotion_modal jpl_promotion_modal"
        backdrop="static"
        keyboard={false}
        scrollable
      >
        <Modal.Body className="promotion_modal_body">
          <ModalCloseButton onClick={() => setShowPromotionModal(false)} />
          {selectedPromotion && (
            <div className="promotion_details_area jpl_promotion_details">
              <div className="promotion_hero_area">
                <div className="promotion_hero_image">
                  {getCardImage(selectedPromotion) && (
                    <img
                      src={getCardImage(selectedPromotion)}
                      alt={getCardHeadline(selectedPromotion)}
                    />
                  )}
                </div>
                <div className="promotion_hero_content">
                  <h1 className="promotion_title">
                    {getCardHeadline(selectedPromotion)}
                  </h1>
                </div>
              </div>
              <div className="promotion_details_content_area">
                {(() => {
                  const desc = getCardDescription(selectedPromotion);
                  if (!desc) return null;
                  const isHtml =
                    typeof desc === "string" &&
                    /<[a-z][\s\S]*>/i.test(desc);
                  return isHtml ? (
                    renderHTML(desc)
                  ) : (
                    <p className="promotion_desc_text">{desc}</p>
                  );
                })()}
              </div>
              <div className="promotion_modal_actions">
                <button
                  type="button"
                  className={`jpl_btn jpl_btn_claim lang-${langCode}`}
                  onClick={handleClaimBonusClick}
                >
                  {__("Claim Bonus")}
                </button>
                <Link
                  to="/bonus-terms"
                  className={`jpl_btn jpl_btn_terms lang-${langCode}`}
                  onClick={() => setShowPromotionModal(false)}
                >
                  {__("Bonus Terms")}
                </Link>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
